import { useCallback, useEffect, useRef } from "react";

function useCurrentRef(current) {
  const ref = useRef(current);
  ref.current = current;
  return ref;
}

export function useStaticCallback(callback) {
  const ref = useCurrentRef(callback);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((...args) => ref.current(...args), []);
}

export function useEventListener(eventKey, callback) {
  const handler = useStaticCallback(callback);

  useEffect(() => {
    window.addEventListener(eventKey, handler);
    return () => {
      window.removeEventListener(eventKey, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * If focus/touch/click happens outside of the provided elements, trigger the callback.
 * A suped-up onClickOutside which accounts for focus and touch.
 *
 * @param {Array<{ current: Element }>} refs - A list of element refs that the event must be outside of.
 * @param {Function} handler
 */
export function useAwayHandler(refs, handler) {
  const listener = (event) => {
    const elements = refs.map((r) => r.current).filter((x) => !!x);
    if (elements.some((el) => el.contains(event.target))) {
      return;
    }
    handler(event);
  };

  useEventListener("touchstart", listener);
  useEventListener("mousedown", listener);
  useEventListener("focusin", listener);
}
